import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getDeveloperCrawlProviders, syncFromDevelopers } from 'api/line'
import { queryKeys } from 'constants/queryKeys'
import { CrawlWizardModal } from './CrawlWizardModal'

type Props = {
  open: boolean
  onClose: () => void
  organizationId: number
}

export function DeveloperSyncModal({ open, onClose, organizationId }: Props) {
  const queryClient = useQueryClient()

  const config = useMemo(() => ({
    title: 'チャンネル連携',
    cookieDescription: (
      <p className="text-sm text-muted mb-4">
        <a href="https://developers.line.biz" target="_blank" rel="noopener noreferrer" className="text-accent underline">developers.line.biz</a> にログイン → DevTools → Application → Cookies → <code className="rounded bg-gray-100 px-1">ses</code> の値をコピーして貼り付けてください。
      </p>
    ),
    cookiePlaceholder: 'ses Cookie の値を貼り付け...',
    providerSelectLabel: '連携するプロバイダーを選択してください。',
    processingLabel: '連携中...',
    fetchProviders: async (cookie: string) => {
      return getDeveloperCrawlProviders(cookie)
    },
    execute: async (cookie: string, providerId: string) => {
      const data = await syncFromDevelopers(cookie, providerId, organizationId)
      return {
        badges: [
          { label: '登録', count: data.registered, color: 'green' as const },
          { label: 'スキップ', count: data.skipped, color: 'gray' as const },
          ...(data.failed > 0 ? [{ label: '失敗', count: data.failed, color: 'red' as const }] : []),
        ],
        items: data.channels.map((ch) => ({
          key: ch.channelId,
          name: ch.name,
          id: ch.basicId || '-',
          status: ch.status === 'failed' ? 'failed' as const : 'success' as const,
          statusLabel: ch.status === 'registered'
            ? `登録${ch.error ?? ''}`
            : ch.status === 'skipped'
              ? (ch.error || 'スキップ')
              : '失敗',
          error: ch.status === 'failed' ? ch.error : undefined,
        })),
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS] }),
  }), [queryClient, organizationId])

  return <CrawlWizardModal open={open} onClose={onClose} config={config} />
}
